import React, { useContext, useEffect, useState } from 'react';
import useScreenSize from '../../../utils/useScreenSize';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import config from 'config';
import Dialog from '../../Dialog/Dialog';
import { onboardingHints } from '../../../constants';
import { aiAssistantContext } from '../../../state/AIAssistantContextProvider';
import { useSelector } from 'react-redux';
import { selectUser } from '../../../reducers/user.selectors';
import { getConversation, getCurrentConversationId } from '../../../reducers/aiAssistant.selectors';
import { StoreType } from '../../../reducers';
import { safeLocalStorage } from '../../../utils/safeStorage';
import useIsPodcastGenerating from './useIsPodcastGenerating';

const AIAssistantFeedbackDialog = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const { isXsScreen } = useScreenSize();
  const { isAssistantOpen, wasFeedbackOpen, setWasFeedbackOpen } = useContext(aiAssistantContext);
  const isPodcastGenerating = useIsPodcastGenerating();
  const user = useSelector(selectUser);
  const [lastConversationId, setLastConversationId] = useState<string | null>(null);
  const currentConversationId = useSelector(getCurrentConversationId);
  const lastConversation = useSelector((state: StoreType) => getConversation(state, lastConversationId));

  const handlePrimaryCTAClick = () => {
    // Open window immediately to avoid Safari popup blocker
    const url = user?.id && lastConversationId 
      ? prepareFeedbackUrl(user.id, lastConversationId) 
      : null;
    
    if (url) {
      window.open(url, '_blank');
    }
    
    setIsOpen(false);
    safeLocalStorage.setItem(onboardingHints.mukiBudeFeedback.notShowAgain, 'true');
  };

  const handleSecondaryCTAClick = () => {
    setIsOpen(false);
    safeLocalStorage.setItem(onboardingHints.mukiBudeFeedback.notShowAgain, 'true');
  };

  useEffect(() => {
    if (currentConversationId) {
      setLastConversationId(currentConversationId);
    }
  }, [currentConversationId]);

  useEffect(() => {
    if (
      !wasFeedbackOpen
        && !isAssistantOpen
        && !isPodcastGenerating
        && lastConversation?.data?.messages
        && lastConversation?.data?.messages.filter(({ role }) => role === 'assistant').length > 0
        && !safeLocalStorage.getItem(onboardingHints.mukiBudeFeedback.notShowAgain)
    ) {
      setWasFeedbackOpen(true);
      setIsOpen(true);
    } else if (isAssistantOpen) {
      setIsOpen(false);
      setWasFeedbackOpen(false);
    }
  }, [lastConversation, isAssistantOpen, isPodcastGenerating, setWasFeedbackOpen, wasFeedbackOpen]);
  
  const htmlBody = (
    <Box sx={{ paddingTop: 0 }}>
      <Typography variant='body1'>
        {t('You just used the Mukibude')} <br />
        {t('Help us with asnwers for 3 short questions')} <br />
        {t('Less than 1 minute')}
      </Typography>
    </Box>
  );
  
  const position: 'right-bottom' | 'center' = isXsScreen ? 'center' : 'right-bottom';
  const dialogProps = {
    id: onboardingHints.mukiBudeFeedback.notShowAgain,
    title: t('How do you like the new Mukibude?'),
    onClose: () => setIsOpen(false),
    primaryCta: {
      label: t('Give feedback'),
      onClick: handlePrimaryCTAClick,
    },
    secondaryCta: {
      label: t('Do not show more'),
      onClick: handleSecondaryCTAClick,
    },
    htmlBody,
    position,
    dataTestId: 'new-ai-assistant-feedback-dialog'
  };
  
  return (
    <Dialog
      open={isOpen}
      {...dialogProps}
    />
  );
};
export default AIAssistantFeedbackDialog;

const prepareFeedbackUrl = (userId: string, conversationId: string) => {
  return config.newAiAssistantFeedbackModal.url.replace('{userId}', userId)
    .replace('{conversationId}', conversationId);
};
